import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { ChevronLeft, Info, Plus, SearchX, UserPlus, UserX } from "lucide-react";

import Filters from "../../../Filter";
import AssignApplicantsPicker from "./AssignApplicantsModal";
import ChangeSupervisorModal from "./ChangeSupervisorModal";
import UnassignSupervisorModal from "./UnassignSupervisorModal";
import {
  UIActionModal,
  UIButton,
  UICallout,
  UICard,
  UIEmptyState,
  UIModal,
  UIPaginationBar,
  UIRating,
  UITable,
  UIUserInfo,
} from "../../../ui";
import useDebounce from "../../../../hooks/useDebounce";
import useEventSupervisorOptions from "../../../../hooks/useEventSupervisorOptions";
import { getSupervisorOptionsForZone } from "../../../../utils/supervisorOptions";
import { genders } from "../../../../utils/Constants";
import { getRoleOptionsFromEvent } from "../Applicants/eventRequestMappers";
import messages from "../../../../assets/locale/messages";
import useZoneApplicants from "./hooks/useZoneApplicants";
import { isAdminZoneApplicantsPortal, ZONE_APPLICANTS_PORTALS } from "./portal";

import "./ZoneApplicants.scss";

const SUPERVISOR_ROLE = "Supervisor";
const SEARCH_DEBOUNCE_MS = 450;
const READ_ONLY_EVENT_STATUSES = ["past", "canceled"];

const ZoneApplicants = ({
  event,
  zoneId,
  portalVariant,
  readOnly,
  onRefreshEvent,
  onBack,
}) => {
  const isAdmin = isAdminZoneApplicantsPortal(portalVariant);
  const lang = useSelector((state) => state.locale.lang);
  const { zoneApplicants, shared } = messages[lang];

  const eventId = event?.id;

  const [searchValue, setSearchValue] = useState("");
  const [filterRole, setFilterRole] = useState(null);
  const [filterGender, setFilterGender] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openUnassignModal, setOpenUnassignModal] = useState(false);
  const [openSupervisorModal, setOpenSupervisorModal] = useState(false);
  const [openUnassignSupervisorModal, setOpenUnassignSupervisorModal] =
    useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedEventRequests, setSelectedEventRequests] = useState([]);

  const appliedSearch = useDebounce(searchValue.trim(), SEARCH_DEBOUNCE_MS);

  const zoneIndex =
    event?.zones?.findIndex((item) => String(item.id) === String(zoneId)) ?? -1;
  const zone = zoneIndex >= 0 ? event.zones[zoneIndex] : undefined;
  const zoneSupervisor = zone?.supervisor;
  const rawSupervisorId = zone?.supervisor_id ?? zoneSupervisor?.id;
  const currentSupervisorId = rawSupervisorId ? String(rawSupervisorId) : "";
  const currentSupervisorName = zoneSupervisor?.["full_name_" + lang] || "";
  // Staffing is off either because the caller has no edit permission or because
  // the event itself is closed to changes.
  const isReadOnly =
    readOnly || READ_ONLY_EVENT_STATUSES.includes(event?.status);

  const {
    rows,
    meta,
    isLoading,
    assignApplicants,
    unassignApplicant,
    changeSupervisor,
    unassignSupervisor,
  } = useZoneApplicants({
    eventId,
    zoneId,
    portalVariant,
    page,
    pageSize,
    search: appliedSearch,
    roleId: filterRole?.id,
    gender: filterGender?.value,
    lang,
    sharedLabels: shared,
    onRefreshEvent,
  });

  // The requests API only returns supervisors assigned to no zone at all, so on
  // a fully staffed event that list is empty. This hook is the app-wide source:
  // it merges that list with the supervisors already sitting on the event zones.
  const allSupervisorOptions = useEventSupervisorOptions({
    eventId,
    requestsPortal: isAdmin ? "admin" : "company",
    initialEvent: event,
    lang,
    // Re-fetches once the zone's supervisor changes, so an unassigned
    // supervisor becomes selectable again without a remount.
    refreshKey: currentSupervisorId,
  });

  // Zones come back with a nested `supervisor` object, but the per-zone filter
  // reads `supervisor_id` — without this projection every other zone's
  // supervisor stays selectable here.
  const zonesWithSupervisorIds = useMemo(
    () =>
      (event?.zones ?? []).map((item) => ({
        ...item,
        supervisor_id: item?.supervisor_id ?? item?.supervisor?.id,
      })),
    [event?.zones]
  );

  // Only supervisors free of every zone can be picked here: the shared helper
  // keeps this zone's own supervisor in the list for the event form, but
  // swapping a supervisor with themselves is not a change.
  const supervisorOptions = useMemo(
    () =>
      getSupervisorOptionsForZone(
        allSupervisorOptions,
        zonesWithSupervisorIds,
        zoneIndex
      )
        .filter((option) => String(option.id) !== currentSupervisorId)
        .map((option) => ({
          value: String(option.id),
          label: option.name,
        })),
    [
      allSupervisorOptions,
      zonesWithSupervisorIds,
      zoneIndex,
      currentSupervisorId,
    ]
  );

  // Supervisors are staffed through the supervisor row, never the applicant picker.
  const assignableRoles = useMemo(() => {
    const englishNameById = new Map(
      getRoleOptionsFromEvent(event, "en").map((role) => [role.id, role.name])
    );

    return getRoleOptionsFromEvent(event, lang).filter(
      (role) => englishNameById.get(role.id) !== SUPERVISOR_ROLE
    );
  }, [event, lang]);

  const genderOptions = useMemo(
    () => [
      { id: 1, name: shared.male, value: genders.male },
      { id: 2, name: shared.female, value: genders.female },
      { id: 3, name: shared.notProvided, value: genders.notProvided },
    ],
    [shared]
  );

  useEffect(() => {
    setPage(1);
  }, [appliedSearch, filterRole, filterGender, pageSize]);

  const handleUnassignClick = useCallback((row) => {
    setSelectedItem(row);
    setOpenUnassignModal(true);
  }, []);

  const handleCloseUnassignModal = () => {
    setOpenUnassignModal(false);
    setSelectedItem(null);
  };

  const handleCloseAssignModal = () => {
    setOpenAssignModal(false);
    setSelectedEventRequests([]);
  };

  const handleCloseSupervisorModal = () => setOpenSupervisorModal(false);

  const handleConfirmUnassign = () => {
    unassignApplicant(selectedItem, { willClearZone });
    handleCloseUnassignModal();
  };

  const handleConfirmAssign = () => {
    assignApplicants(selectedEventRequests);
    handleCloseAssignModal();
  };

  const handleConfirmSupervisor = (supervisorId) => {
    changeSupervisor(supervisorId, { isChange: hasZoneSupervisor });
    handleCloseSupervisorModal();
  };

  const handleConfirmUnassignSupervisor = () => {
    unassignSupervisor();
    setOpenUnassignSupervisorModal(false);
  };

  const columns = useMemo(() => {
    const cols = [
      {
        field: "userId",
        headerName: shared.id,
        sortable: false,
        minWidth: 110,
        cellClassName: "zone-applicants__id-cell",
        renderCell: ({ value }) => (
          <span className="zone-applicants__id MuiDataGrid-cellContent">
            {value ? `#${value}` : "—"}
          </span>
        ),
      },
      {
        field: "name",
        headerName: zoneApplicants.staffApplicant,
        sortable: false,
        minWidth: 220,
        renderCell: ({ row }) => (
          <UIUserInfo
            name={row.name || "-"}
            image={row.image}
            className="zone-applicants__user"
          />
        ),
      },
      {
        field: "requestedRole",
        headerName: shared.role,
        sortable: false,
        renderCell: ({ value }) => (
          <span className="MuiDataGrid-cellContent">{value || "-"}</span>
        ),
      },
      {
        field: "genderLabel",
        headerName: shared.gender,
        sortable: false,
        renderCell: ({ value }) => (
          <span className="MuiDataGrid-cellContent">{value}</span>
        ),
      },
      {
        field: "rating",
        headerName: zoneApplicants.rating,
        sortable: false,
        renderCell: ({ value }) => (
          <UIRating value={value ?? 0} variant="compact" />
        ),
      },
    ];

    if (!isReadOnly) {
      cols.push({
        field: "actions",
        headerName: zoneApplicants.action,
        sortable: false,
        align: "center",
        headerAlign: "center",
        width: 150,
        cellClassName: "zone-applicants__actions-cell",
        renderCell: ({ row }) => (
          <UIButton
            type="button"
            variant="destructive"
            appearance="outline"
            size="small"
            onClick={() => handleUnassignClick(row)}
          >
            {zoneApplicants.unassign}
          </UIButton>
        ),
      });
    }

    return cols;
  }, [zoneApplicants, shared, isReadOnly, handleUnassignClick]);

  const total = meta?.count || 0;
  const currentPage = meta?.currentPage || page;
  const rangeFrom = total > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeTo = Math.min(currentPage * pageSize, total);

  const hasActiveSearchOrFilters = Boolean(
    appliedSearch || filterRole || filterGender
  );

  // Unfiltered, the list total *is* the number of applicants in the zone. Only a
  // filtered view needs the zone's own counter — and `number_of_approved_requests`
  // is the only field that counts assignments (the others are the requested
  // headcount), so an absent one falls back to the list.
  const zoneApplicantsCount = hasActiveSearchOrFilters
    ? Number(zone?.number_of_approved_requests ?? total)
    : total;
  // A zone counts as cleared only once nobody is left on it — the supervisor
  // keeps it staffed even after the last applicant goes.
  const hasZoneSupervisor = Boolean(currentSupervisorId);
  const willClearZone = zoneApplicantsCount === 1 && !hasZoneSupervisor;

  // The zone is the only part that can run long, so it carries the ellipsis and
  // keeps the id and the role visible on one line.
  const unassignZoneName = selectedItem?.zone || zone?.name;
  const unassignSummaryParts = [
    selectedItem?.userId ? `#${selectedItem.userId}` : null,
    unassignZoneName ? (
      <span
        key="zone"
        className="zone-applicants__unassign-zone"
        title={unassignZoneName}
      >
        {unassignZoneName}
      </span>
    ) : null,
    selectedItem?.requestedRole,
  ].filter(Boolean);

  const unassignSummary = unassignSummaryParts.length
    ? unassignSummaryParts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 ? " · " : null}
          {part}
        </React.Fragment>
      ))
    : null;

  const renderApplicantsList = () => {
    if (!isLoading && !rows.length) {
      return hasActiveSearchOrFilters ? (
        <UIEmptyState
          classNames={{ root: "zone-applicants__empty" }}
          icon={<SearchX size={40} />}
          title={zoneApplicants.filteredEmptyState.title}
          description={zoneApplicants.filteredEmptyState.description}
        />
      ) : (
        <UIEmptyState
          classNames={{ root: "zone-applicants__empty" }}
          icon={<UserX size={40} />}
          title={zoneApplicants.emptyState.title}
          description={zoneApplicants.emptyState.description}
          actionLabel={isReadOnly ? null : zoneApplicants.assignApplicants}
          actionIcon={<UserPlus size={18} />}
          onAction={isReadOnly ? undefined : () => setOpenAssignModal(true)}
        />
      );
    }

    return (
      <>
        <UITable
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          contentSized
          hideFooter
          disableSelectionOnClick
        />
        {total > 0 && (
          <UIPaginationBar
            currentPage={currentPage}
            pages={meta?.pages || 1}
            pageSize={pageSize}
            rangeTemplate={zoneApplicants.showingRange}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            rangeTotal={total}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            className="zone-applicants__pagination"
          />
        )}
      </>
    );
  };

  return (
    <div className="zone-applicants">
      {onBack && (
        <UIButton
          type="button"
          variant="text"
          className="zone-applicants__back"
          startIcon={<ChevronLeft size={18} />}
          onClick={onBack}
        >
          {zone?.name || zoneApplicants.applicants}
        </UIButton>
      )}

      <Filters
        isCollapsable
        filters={[
          {
            type: "filter",
            name: "role",
            placeholder: shared.role,
            value: filterRole,
            options: assignableRoles,
            onChange: (value) => setFilterRole(value),
          },
          {
            type: "filter",
            name: "gender",
            placeholder: shared.gender,
            value: filterGender,
            options: genderOptions,
            onChange: (value) => setFilterGender(value),
          },
        ]}
        searchInput={{
          placeholder: zoneApplicants.search,
          name: "search",
          value: searchValue,
          onChange: (value) => setSearchValue(value),
        }}
        renderActionBeforeFilterToggle={
          isReadOnly ? undefined : (
            <UIButton
              type="button"
              variant="primary"
              appearance="outline"
              className="zone-applicants__assign-action"
              startIcon={<Plus size={18} />}
              onClick={() => setOpenAssignModal(true)}
            >
              {zoneApplicants.assignApplicants}
            </UIButton>
          )
        }
        classNames={{
          container: "zone-applicants__filters",
          base: "",
          search: "",
          filter: "",
        }}
      />

      <UICard className="zone-applicants__supervisor" type="info">
        <Box className="zone-applicants__supervisor-row">
          <UIUserInfo
            name={currentSupervisorName || zoneApplicants.notAssigned}
            description={zoneApplicants.supervisor}
            descriptionPlacement="top"
            image={zoneSupervisor?.image}
            className="zone-applicants__supervisor-user"
          />
          {!isReadOnly && (
            <Box className="zone-applicants__supervisor-actions">
              <UIButton
                type="button"
                variant="outlined"
                appearance="outline"
                onClick={() => setOpenSupervisorModal(true)}
              >
                {hasZoneSupervisor
                  ? zoneApplicants.changeSupervisor
                  : zoneApplicants.assignSupervisor}
              </UIButton>
              {hasZoneSupervisor && (
                <UIButton
                  type="button"
                  variant="destructive"
                  appearance="outline"
                  onClick={() => setOpenUnassignSupervisorModal(true)}
                >
                  {zoneApplicants.unassignSupervisor}
                </UIButton>
              )}
            </Box>
          )}
        </Box>
      </UICard>

      <div className="zone-applicants__content">{renderApplicantsList()}</div>

      <UIModal
        open={openAssignModal}
        onClose={handleCloseAssignModal}
        type="info"
        title={zoneApplicants.assignModal.title}
        maxWidth="sm"
        className="zone-applicants__assign-modal"
        actions={
          <Box className="zone-applicants__modal-actions">
            <UIButton
              variant="outlined"
              appearance="outline"
              fullWidth
              onClick={handleCloseAssignModal}
            >
              {shared.cancel}
            </UIButton>
            <UIButton
              appearance="gradient"
              fullWidth
              disabled={!selectedEventRequests.length}
              onClick={handleConfirmAssign}
            >
              {zoneApplicants.assignSelected}
            </UIButton>
          </Box>
        }
      >
        <AssignApplicantsPicker
          eventId={eventId}
          portalVariant={portalVariant}
          roles={assignableRoles}
          selectedIds={selectedEventRequests}
          onSelectItems={setSelectedEventRequests}
        />
      </UIModal>

      <UIActionModal
        open={openUnassignModal}
        onClose={handleCloseUnassignModal}
        variant="reject"
        title={zoneApplicants.unassignModal.title}
        className="zone-applicants__unassign-modal"
        actions={
          <UIActionModal.Actions
            cancelLabel={shared.cancel}
            confirmLabel={zoneApplicants.unassign}
            confirmVariant="reject"
            onCancel={handleCloseUnassignModal}
            onConfirm={handleConfirmUnassign}
          />
        }
      >
        <Typography className="zone-applicants__unassign-question">
          {zoneApplicants.unassignModal.unassignConfirmationMsg}
        </Typography>

        {selectedItem && (
          <UIActionModal.UserInfo
            name={selectedItem.name || "-"}
            image={selectedItem.image}
            description={unassignSummary}
          />
        )}

        {willClearZone && (
          <UICallout
            variant="error"
            icon={<Info size={20} strokeWidth={2} className="ui-callout__icon" />}
            title={zoneApplicants.unassignModal.zoneWillBeCleared}
            description={zoneApplicants.unassignModal.statusChange}
            hasBorder={false}
          />
        )}
      </UIActionModal>

      <ChangeSupervisorModal
        open={openSupervisorModal}
        currentSupervisorId={currentSupervisorId}
        currentSupervisorName={currentSupervisorName}
        options={supervisorOptions}
        onClose={handleCloseSupervisorModal}
        onConfirm={handleConfirmSupervisor}
      />

      <UnassignSupervisorModal
        open={openUnassignSupervisorModal}
        supervisorName={currentSupervisorName}
        supervisorImage={zoneSupervisor?.image}
        supervisorUserId={rawSupervisorId}
        zoneName={zone?.name}
        onClose={() => setOpenUnassignSupervisorModal(false)}
        onConfirm={handleConfirmUnassignSupervisor}
      />
    </div>
  );
};

ZoneApplicants.propTypes = {
  event: PropTypes.object,
  zoneId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  portalVariant: PropTypes.oneOf(Object.values(ZONE_APPLICANTS_PORTALS)),
  /** Hides assign, unassign, and change-supervisor. */
  readOnly: PropTypes.bool,
  onRefreshEvent: PropTypes.func,
  /** Renders a back control above the toolbar when the list is shown inline. */
  onBack: PropTypes.func,
};

ZoneApplicants.defaultProps = {
  event: null,
  zoneId: undefined,
  portalVariant: ZONE_APPLICANTS_PORTALS.company,
  readOnly: false,
  onRefreshEvent: undefined,
  onBack: undefined,
};

export default ZoneApplicants;
